import { ArrowDown, ArrowUp, GripVertical, Plus, Trash2 } from 'lucide-react';
import { useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { useWatch, type UseFieldArrayReturn, type UseFormReturn } from 'react-hook-form';
import type { CareerSiteSettings } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Field } from '../../../../components/Field';
import { Input, Textarea } from '../../../../components/Input';
import { useToasts } from '../../../../stores/toasts';
import styles from './SectionsEditor.module.css';

/** The public site shows at most four (the shared schema says the same). */
export const MAX_SECTIONS = 4;

interface Drag {
  readonly from: number;
  readonly to: number;
}

/**
 * "Sections under the job list" (Option A): up to four, in the order the site shows them. Drag a section by its handle,
 * or move it with Move up / Move down — or Alt and the arrow keys on the handle (06-qol §2). Removing one takes it out
 * of the draft at once, with Undo; nothing reaches the site until Save.
 */
export function SectionsEditor({
  form,
  sections,
  canEdit,
}: {
  readonly form: UseFormReturn<CareerSiteSettings>;
  readonly sections: UseFieldArrayReturn<CareerSiteSettings, 'sections'>;
  readonly canEdit: boolean;
}): ReactNode {
  const showUndo = useToasts((state) => state.showUndo);
  const show = useToasts((state) => state.show);
  const list = useRef<HTMLOListElement | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const titles = useWatch({ control: form.control, name: 'sections' });
  const errors = form.formState.errors.sections;
  const count = sections.fields.length;

  const titleOf = (index: number): string => titles?.[index]?.title?.trim() || `Section ${index + 1}`;

  function move(from: number, to: number): void {
    if (to < 0 || to >= count || to === from) return;
    sections.move(from, to);
  }

  function removeAt(index: number): void {
    const removed = form.getValues(`sections.${index}`);
    const name = titleOf(index);
    sections.remove(index);
    showUndo(`“${name}” removed`, () => {
      const now = form.getValues('sections').length;
      // Another section took its place in the meantime: putting it back would make five, which the site never shows.
      if (now >= MAX_SECTIONS) {
        show({ tone: 'warning', message: `“${name}” can't come back`, detail: `There are ${MAX_SECTIONS} sections already. Remove one first.` });
        return;
      }
      sections.insert(Math.min(index, now), removed);
    });
  }

  /** The board's pointer drag (EditableTable): the drop lands where the pointer passes a card's middle. */
  function startDrag(event: ReactPointerEvent<HTMLButtonElement>, from: number): void {
    if (!canEdit) return;
    event.preventDefault();
    const handle = event.currentTarget;
    // Captured, so the drag follows the pointer past the handle; an old browser without capture still gets the drop.
    handle.setPointerCapture?.(event.pointerId);
    setDrag({ from, to: from });
    const cards = Array.from(list.current?.children ?? []);
    const targetAt = (clientY: number): number => {
      const index = cards.findIndex((card) => {
        const box = card.getBoundingClientRect();
        return clientY < box.top + box.height / 2;
      });
      return index === -1 ? cards.length - 1 : index;
    };
    const onMove = (moveEvent: PointerEvent): void => setDrag({ from, to: targetAt(moveEvent.clientY) });
    const onEnd = (endEvent: PointerEvent): void => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onEnd);
      handle.removeEventListener('pointercancel', onEnd);
      setDrag(null);
      if (endEvent.type === 'pointerup') move(from, targetAt(endEvent.clientY));
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onEnd);
    handle.addEventListener('pointercancel', onEnd);
  }

  return (
    <div className={styles.sections}>
      <div className={styles.head}>
        <h2 className={styles.title}>Sections under the job list</h2>
        <span className={styles.count}>
          {count} of {MAX_SECTIONS}
        </span>
      </div>
      {count === 0 && <p className={styles.muted}>For example “Why join us” or “How we hire”. They show under the list of roles.</p>}
      {count > 0 && (
        <ol ref={list} className={styles.list}>
          {sections.fields.map((section, index) => {
            const name = titleOf(index);
            const isDragged = drag?.from === index;
            const isDropTarget = drag !== null && drag.to === index && drag.from !== index;
            return (
              <li
                key={section.id}
                className={styles.section}
                data-dragged={isDragged || undefined}
                data-drop={isDropTarget ? (drag.to > drag.from ? 'below' : 'above') : undefined}
              >
                <div className={styles.sectionHead}>
                  {canEdit && (
                    <button
                      type="button"
                      className={styles.handle}
                      aria-label={`Move “${name}”. Drag it, or press Alt and an arrow key`}
                      onPointerDown={(event) => startDrag(event, index)}
                      onKeyDown={(event) => {
                        if (!event.altKey || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return;
                        event.preventDefault();
                        move(index, index + (event.key === 'ArrowUp' ? -1 : 1));
                      }}
                    >
                      <GripVertical size={16} aria-hidden />
                    </button>
                  )}
                  <span className={styles.sectionName}>{name}</span>
                  {canEdit && (
                    <span className={styles.sectionActions}>
                      <Button size="sm" variant="ghost" icon={ArrowUp} aria-label={`Move “${name}” up`} disabled={index === 0} onClick={() => move(index, index - 1)} />
                      <Button size="sm" variant="ghost" icon={ArrowDown} aria-label={`Move “${name}” down`} disabled={index === count - 1} onClick={() => move(index, index + 1)} />
                      <Button size="sm" variant="ghost" icon={Trash2} aria-label={`Remove “${name}”`} onClick={() => removeAt(index)} />
                    </span>
                  )}
                </div>
                <Field label="Title" isRequired error={errors?.[index]?.title?.message}>
                  {({ inputId, describedBy, isInvalid }) => (
                    <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} maxLength={80} {...form.register(`sections.${index}.title`)} />
                  )}
                </Field>
                <Field label="Text" isRequired hint="Lines starting with “- ” become a list." error={errors?.[index]?.body?.message}>
                  {({ inputId, describedBy, isInvalid }) => (
                    <Textarea id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} rows={4} maxLength={3000} {...form.register(`sections.${index}.body`)} />
                  )}
                </Field>
              </li>
            );
          })}
        </ol>
      )}
      {canEdit && count < MAX_SECTIONS && (
        <Button size="sm" variant="quiet" icon={Plus} onClick={() => sections.append({ title: '', body: '' })}>
          Add a section
        </Button>
      )}
    </div>
  );
}
